import { useState } from "react";
import { api, apiError, imgUrl } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { CalendarHeart, Calendar, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => new Date(d).toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

function EventRow({ ev, onPick, busy }) {
  return (
    <div data-testid={`second-date-event-${ev.id}`} className="glass rounded-2xl p-3 flex items-center gap-3">
      <img src={imgUrl(ev.cover_image)} alt={ev.title} className="h-14 w-14 rounded-xl object-cover shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm truncate">{ev.title}</p>
        <p className="text-[11px] text-amber-300/80 flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmt(ev.date_time)}</p>
        <p className="text-[11px] text-emerald-100/60 truncate">{ev.venue}</p>
      </div>
      <button data-testid={`second-date-suggest-${ev.id}`} disabled={busy} onClick={() => onPick(ev)}
        className="btn-gold rounded-full h-9 px-4 text-xs font-semibold disabled:opacity-50 shrink-0">Suggest</button>
    </div>
  );
}

export function SecondDateButton({ matchId, onProposed }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setOpen(true); setData(null);
    try { setData((await api.get(`/matches/${matchId}/second-date-ideas`)).data); }
    catch (e) { setData({ events: [] }); toast.error(apiError(e.response?.data?.detail) || "Couldn't load ideas"); }
  };
  const pick = async (ev) => {
    setBusy(true);
    try { onProposed((await api.post(`/matches/${matchId}/second-date`, { event_id: ev.id })).data); setOpen(false); toast.success(`Suggested ${ev.title}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't send"); }
    setBusy(false);
  };
  return (
    <>
      <button data-testid="second-date-btn" onClick={load}
        className="mt-2 w-full h-9 rounded-full text-xs font-semibold flex items-center justify-center gap-2 border border-amber-400/40 text-amber-100 hover:bg-amber-400/10 transition-colors">
        <CalendarHeart className="h-4 w-4" /> Plan a second date
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" data-testid="second-date-sheet" className="glass border-amber-500/20 max-h-[85vh] overflow-y-auto rounded-t-3xl">
          <SheetHeader className="text-left">
            <SheetTitle className="font-display">Plan a second date</SheetTitle>
            <SheetDescription className="text-emerald-100/60">The soonest events near you. Suggest one and your match can say yes.</SheetDescription>
          </SheetHeader>
          {data?.chosen && <p data-testid="second-date-chosen" className="mt-3 text-xs text-emerald-300">Booked: <b>{data.chosen.title}</b></p>}
          <div className="space-y-2.5 mt-4 pb-4">
            {!data && <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-300" /></div>}
            {data && data.events.length === 0 && <p data-testid="second-date-empty" className="text-sm text-emerald-100/60 text-center py-8">No upcoming events nearby yet — check back soon.</p>}
            {data?.events.map((ev) => <EventRow key={ev.id} ev={ev} onPick={pick} busy={busy} />)}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

const STATUS = { accepted: "It's a date ✓ You're both going", declined: "Maybe a different one", replaced: "Replaced by a newer idea" };

export function SecondDateCard({ m, mine, matchId, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const { event: ev, status } = m.second_date;
  const respond = async (accept) => {
    setBusy(true);
    try { onUpdate(m.id, (await api.post(`/matches/${matchId}/second-date/${m.id}/respond`, { accept })).data.second_date); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't respond"); }
    setBusy(false);
  };
  return (
    <div data-testid="second-date-card" className={`max-w-[80%] rounded-[18px] overflow-hidden border ${mine ? "border-amber-400/40 bg-amber-400/10 rounded-br-md" : "border-sky-400/30 bg-sky-400/5 rounded-bl-md"}`}>
      <img src={imgUrl(ev.cover_image)} alt={ev.title} className="h-24 w-full object-cover" />
      <div className="p-3.5">
        <p className="text-[10px] uppercase tracking-wide text-amber-300/80 flex items-center gap-1"><CalendarHeart className="h-3 w-3" /> Second date idea</p>
        <p className="font-display font-bold mt-1">{ev.title}</p>
        <p className="text-[11px] text-emerald-100/60">{fmt(ev.date_time)} · {ev.venue}</p>
        {ev.ticket_url?.startsWith("https://") && <a href={ev.ticket_url} target="_blank" rel="noopener noreferrer" data-testid="second-date-tickets" className="text-[11px] text-sky-300 hover:text-sky-200">Get tickets</a>}
        {status === "proposed" && !mine ? (
          <div className="flex gap-2 mt-2.5">
            <button data-testid="second-date-accept" disabled={busy} onClick={() => respond(true)} className="btn-gold rounded-full h-8 px-3.5 text-xs font-semibold flex items-center gap-1"><Check className="h-3.5 w-3.5" /> I'm in</button>
            <button data-testid="second-date-decline" disabled={busy} onClick={() => respond(false)} className="rounded-full h-8 px-3.5 text-xs border border-white/15 text-white/70 flex items-center gap-1"><X className="h-3.5 w-3.5" /> Another one</button>
          </div>
        ) : (
          <p data-testid="second-date-status" className={`text-xs mt-2 font-semibold ${status === "accepted" ? "text-emerald-300" : "text-white/50"}`}>
            {status === "proposed" ? "Waiting for a reply…" : STATUS[status]}
          </p>
        )}
      </div>
    </div>
  );
}
